import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { apiRequest, extractApiFieldErrors, ApiError } from '@/lib/api-client';
import { HEDGING_ALERTS_QUERY_KEY } from './alerts';
import { HEDGING_EXPOSURE_QUERY_KEY } from './exposure';
import { HEDGING_HEDGE_BOOK_QUERY_KEY } from './hedge-book';
import { HEDGING_PENDING_REFERENCES_QUERY_KEY } from './pending-references';
import { HEDGING_POSITION_MATCHING_QUERY_KEY } from './position-matching';
import { HEDGING_QP_ALIGNMENT_QUERY_KEY } from './qp-alignment';
import { HEDGING_RECONCILIATION_BY_TRADE_QUERY_KEY } from './reconciliation-by-trade';
import { HEDGING_ROLL_MONTHS_QUERY_KEY } from './roll-months';
import { HEDGING_SPREAD_PL_QUERY_KEY } from './spread-pl';

export interface CreateExecutionAllocationPayload {
  hedgeRequestId: string;
  quantityMt: string;
}

export interface CreateExecutionPayload {
  executionDate: string;
  promptDate?: string;
  executedPrice: string;
  executedPriceCurrency?: string;
  instrument?: string;
  exchange?: string;
  brokerName?: string;
  contractReference?: string;
  fixingPrice?: string;
  notes?: string;
  // When set (the StoneX position-matching flow), the backend requires the
  // allocations to cover this quantity exactly — no residual on the position.
  positionQuantityMt?: string;
  allocations: CreateExecutionAllocationPayload[];
}

export interface CreatedExecutionView {
  id: string;
  hedgeRequestId: string | null;
  executionType: string;
}

export interface SoftDeleteExecutionPayload {
  id: string;
  deleteReason: string;
}

export const EXECUTION_INVALIDATE_KEYS: readonly QueryKey[] = [
  [HEDGING_HEDGE_BOOK_QUERY_KEY],
  HEDGING_EXPOSURE_QUERY_KEY,
  HEDGING_QP_ALIGNMENT_QUERY_KEY,
  HEDGING_ROLL_MONTHS_QUERY_KEY,
  HEDGING_POSITION_MATCHING_QUERY_KEY,
  HEDGING_RECONCILIATION_BY_TRADE_QUERY_KEY,
  HEDGING_SPREAD_PL_QUERY_KEY,
  HEDGING_ALERTS_QUERY_KEY,
  HEDGING_PENDING_REFERENCES_QUERY_KEY,
];

function invalidateExecutionQueries(queryClient: QueryClient): Promise<unknown> {
  return Promise.all(
    EXECUTION_INVALIDATE_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

export function useCreateHedgeExecution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExecutionPayload) =>
      apiRequest<CreatedExecutionView>('/hedging/executions', { method: 'POST', body: input }),
    onSuccess: () => invalidateExecutionQueries(queryClient),
  });
}

export interface UpdateExecutionPayload {
  id: string;
  contractReference: string | null;
}

export function useUpdateHedgeExecution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contractReference }: UpdateExecutionPayload) =>
      apiRequest<unknown>(`/hedging/executions/${id}`, {
        method: 'PATCH',
        body: { contractReference },
      }),
    onSuccess: () => invalidateExecutionQueries(queryClient),
  });
}

export function useSoftDeleteHedgeExecution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, deleteReason }: SoftDeleteExecutionPayload) =>
      apiRequest<unknown>(`/hedging/executions/${id}`, {
        method: 'DELETE',
        body: { deleteReason },
      }),
    onSuccess: () => invalidateExecutionQueries(queryClient),
  });
}

export interface RecordRollPayload {
  hedgeRequestId: string;
  executionDate: string;
  quantityMt: string;
  closeExecutedPrice: string;
  openExecutedPrice: string;
  closeContractReference?: string;
  openContractReference?: string;
  executedPriceCurrency?: string;
  brokerName?: string;
  exchange?: string;
  notes?: string;
}

export interface RollPairResponse {
  closeExecution: CreatedExecutionView;
  openExecution: CreatedExecutionView;
}

export function useRecordRoll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordRollPayload) =>
      apiRequest<RollPairResponse>('/hedging/executions/roll', { method: 'POST', body: input }),
    onSuccess: () => invalidateExecutionQueries(queryClient),
  });
}

// The flat `message` an error body carries, whether a string or Nest's string[].
function bodyMessage(record: Record<string, unknown>): string | null {
  const message = record['message'];
  if (typeof message === 'string' && message.length > 0) return message;
  if (Array.isArray(message) && message.every((m) => typeof m === 'string')) {
    return message.join('; ');
  }
  return null;
}

function apiErrorMessage(error: ApiError): string {
  const fallback = `Request failed (${String(error.status)})`;
  const body = error.body;
  if (body === null || typeof body !== 'object') return fallback;
  // Field detail first — the shared reader, so this surface and every other one
  // report a rejected body the same way (#1485 review).
  return extractApiFieldErrors(body) ?? bodyMessage(body as Record<string, unknown>) ?? fallback;
}

export function extractExecutionApiMessage(error: unknown): string {
  if (error instanceof ApiError) return apiErrorMessage(error);
  if (error instanceof Error) return error.message;
  return 'Request failed';
}
